import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/api/client'

const nullableString = z.string().nullable().optional()

const countrySchema = z
  .object({
    id: z.number(),
    name: z.string(),
    image_path: nullableString,
  })
  .passthrough()

const leagueSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    image_path: nullableString,
  })
  .passthrough()

const teamSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    logo_url: nullableString,
  })
  .passthrough()

const countriesResponseSchema = z.object({
  countries: z.array(countrySchema),
})

const leaguesResponseSchema = z.object({
  leagues: z.array(leagueSchema),
})

const teamsResponseSchema = z.object({
  teams: z.array(teamSchema),
})

export type RegistrationCountry = z.infer<typeof countrySchema>
export type RegistrationLeague = z.infer<typeof leagueSchema>
export type RegistrationTeam = z.infer<typeof teamSchema>

async function fetchCountries() {
  const response = await api.get<unknown>('/countries')
  const parsed = countriesResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[registration] Invalid countries payload', parsed.error.flatten())
    throw new Error('Landen konden niet worden geladen.')
  }

  return parsed.data.countries
}

async function fetchLeagues(countryId: number) {
  const response = await api.get<unknown>(`/leagues?country_id=${countryId}`)
  const parsed = leaguesResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[registration] Invalid leagues payload', parsed.error.flatten())
    throw new Error('Competities konden niet worden geladen.')
  }

  return parsed.data.leagues
}

async function fetchTeams(leagueId: number) {
  const response = await api.get<unknown>(`/teams?league_id=${leagueId}`)
  const parsed = teamsResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[registration] Invalid teams payload', parsed.error.flatten())
    throw new Error('Teams konden niet worden geladen.')
  }

  return parsed.data.teams
}

export function useRegistrationCountries() {
  return useQuery({
    queryKey: ['registration', 'countries'],
    queryFn: fetchCountries,
    staleTime: 1000 * 60 * 60,
  })
}

export function useRegistrationLeagues(countryId: number | null) {
  return useQuery({
    queryKey: ['registration', 'leagues', countryId],
    queryFn: () => fetchLeagues(countryId as number),
    enabled: countryId != null,
    staleTime: 1000 * 60 * 30,
  })
}

export function useRegistrationTeams(leagueId: number | null) {
  return useQuery({
    queryKey: ['registration', 'teams', leagueId],
    queryFn: () => fetchTeams(leagueId as number),
    enabled: leagueId != null,
    staleTime: 1000 * 60 * 30,
  })
}
